/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, January 24th 2023, 12:34:41 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { SliderMark } from "@mui/material/Slider";
import Tooltip from "@mui/material/Tooltip";
import { ProcessDefinition } from "lib/schemas";
import { useServiceBuilderContext } from "service-builder/providers";
import { setNewStatusForCurrentProcessDefinition } from "service-builder/providers/actionCreators";
import { CustomSlider2 } from "ui-widgets";
import { StatusUIProperties } from "./ProcessDefinitionUIProperties";

const marks = [
  {
    value: 0,
    label: "",
  },
  {
    value: 50,
    label: "",
  },
  {
    value: 100,
    label: "",
  },
];

function valuetext(value: number) {
  return getStatusFromValue(value)?.label || value.toString();
}

const getStatusFromValue = (value: number): { status: ProcessDefinition["status"]; label: string } => {
  switch (value) {
    case 0:
      return { status: "DEVELOPMENT", label: "Dev" };
    case 50:
      return { status: "PRE_DEPLOYMENT_TESTING", label: "Test" };
    case 100:
      return { status: "DEPLOYED", label: "Live" };
    default:
      return { status: "DEVELOPMENT", label: "Dev" };
  }
};

function valueLabelFormat(value: number) {
  return getStatusFromValue(value)?.label;
}

const MarkComponent = (props: any) => {
  const { children, slot, ...other } = props;
  // let backgroundColor: string;
  let toolTipTitle: string;
  switch (props?.["data-index"]) {
    case 0:
      // backgroundColor = StatusUIProperties?.DEVELOPMENT?.color;
      toolTipTitle = StatusUIProperties?.DEVELOPMENT?.label;
      break;
    case 1:
      // backgroundColor = StatusUIProperties?.PRE_DEPLOYMENT_TESTING?.color;
      toolTipTitle = StatusUIProperties?.PRE_DEPLOYMENT_TESTING?.label;
      break;
    case 2:
      // backgroundColor = StatusUIProperties?.DEPLOYED?.color;
      toolTipTitle = StatusUIProperties?.DEPLOYED?.label;
      break;
    default:
      // backgroundColor = StatusUIProperties?.DEFAULT?.color;
      toolTipTitle = StatusUIProperties?.DEFAULT?.label;
  }

  return (
    <Tooltip title={toolTipTitle}>
      {/* <SliderMark {...other} style={{...other?.style, backgroundColor}}/> */}
      <SliderMark
        {...other}
        style={{ ...other?.style, width: 10, borderRadius: 12, boxShadow: "0px 0px 15px 0px rgb(0 0 0 / 40%)" }}
      />
    </Tooltip>
  );
};

interface ProcessDefinitionStatusSliderProps {
  onChangeStatus: () => void;
}

export const ProcessDefinitionStatusSlider = (props: ProcessDefinitionStatusSliderProps) => {
  const { sliderColor, value, onChangeSliderValue } = useProcessDefinitionStatusSlider(props);

  return (
    <CustomSlider2
      aria-label="Process Definition Status"
      value={value}
      valueLabelFormat={valueLabelFormat}
      getAriaValueText={valuetext}
      step={null}
      valueLabelDisplay="auto"
      marks={marks}
      size="small"
      onChange={onChangeSliderValue}
      sliderColor={sliderColor}
      components={{ Mark: MarkComponent }}
    />
  );
};

const useProcessDefinitionStatusSlider = ({ onChangeStatus }: ProcessDefinitionStatusSliderProps) => {
  const {
    dispatchServiceBuilderAction,
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();
  const onChangeSliderValue = (e, value: number) => {
    const newStatus = getStatusFromValue(value)?.status;
    currentProcessDefinition?.status !== newStatus && onChangeStatus();
    dispatchServiceBuilderAction(setNewStatusForCurrentProcessDefinition(newStatus));
  };

  let value: number;
  let sliderColor: React.CSSProperties["color"];
  switch (currentProcessDefinition?.status) {
    case "DEVELOPMENT":
      value = 0;
      sliderColor = StatusUIProperties.DEVELOPMENT.color;
      break;
    case "PRE_DEPLOYMENT_TESTING":
      value = 50;
      sliderColor = StatusUIProperties.PRE_DEPLOYMENT_TESTING.color;
      break;
    case "DEPLOYED":
      value = 100;
      sliderColor = StatusUIProperties.DEPLOYED.color;
      break;
    default:
      value = null;
      sliderColor = StatusUIProperties.DEFAULT.color;
  }

  return { sliderColor, value, onChangeSliderValue };
};
